/**
 * Framework-agnostic helpers for work-report draft optimistic-lock conflict
 * detection and self-healing. Kept free of Vue imports so they can be unit-tested
 * with the built-in node:test runner (see workReportDraftRecovery.test.mjs).
 */

/**
 * Detect an optimistic-lock version conflict from a rejected API error.
 * The axios response interceptor attaches the backend business code (409) and the
 * HTTP status/response, so a conflict can be recognized and self-healed instead of
 * surfacing as a generic failure. All checks are defensive because different layers
 * may attach the signal differently.
 * @param {Error} error - Rejected error from the API layer.
 * @returns {boolean} True when the error signals a version conflict.
 */
export function isVersionConflict(error) {
  return error?.response?.status === 409
    || error?.status === 409
    || error?.code === 409
    || error?.code === 'VERSION_CONFLICT'
    || error?.response?.data?.code === 409
    || error?.response?.data?.code === 'VERSION_CONFLICT'
}

/**
 * Wrap a draft-save callback with optimistic-lock conflict self-healing.
 *
 * On a version conflict it calls `resync()` to pull the latest optimistic version into
 * the caller's report state, then retries the SAME user edits with a freshly-read
 * expectedVersion and a new idempotency key (clientRequestId), up to `maxConflictRetries`
 * times. A new idempotency key is required because the failed attempt's key must not be
 * reused. When recovery is impossible (no resync provided, resync resolves false because
 * e.g. the report status advanced, or retries are exhausted) the original conflict error
 * is rethrown so the caller can prompt the user to reload.
 *
 * @param {Object} options
 * @param {(payload: Object) => Promise<Object>} options.save - Underlying save call.
 * @param {() => Promise<boolean>} [options.resync] - Refresh latest version; true when safe to retry.
 * @param {() => number} options.getExpectedVersion - Reads the latest optimistic version.
 * @param {() => string} options.createClientRequestId - Creates a fresh idempotency key.
 * @param {number} [options.maxConflictRetries=2] - Max automatic recovery retries per save.
 * @returns {(payload: Object) => Promise<Object>} Wrapped save callback.
 */
export function createConflictRecoverySave({
  save,
  resync,
  getExpectedVersion,
  createClientRequestId,
  maxConflictRetries = 2
}) {
  if (typeof save !== 'function') {
    throw new TypeError('save must be a function')
  }
  if (typeof getExpectedVersion !== 'function') {
    throw new TypeError('getExpectedVersion must be a function')
  }
  if (typeof createClientRequestId !== 'function') {
    throw new TypeError('createClientRequestId must be a function')
  }

  return async (payload) => {
    let currentPayload = payload
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await save(currentPayload)
      } catch (error) {
        const recoverable = isVersionConflict(error)
          && typeof resync === 'function'
          && attempt < maxConflictRetries
        if (!recoverable) {
          throw error
        }
        const recovered = await resync().catch(() => false)
        if (!recovered) {
          throw error
        }
        // Retry the same user edits with a fresh version and a new idempotency key.
        currentPayload = {
          ...currentPayload,
          expectedVersion: getExpectedVersion(),
          clientRequestId: createClientRequestId()
        }
      }
    }
  }
}
